/**
 * Shared number/money formatting for the GardEn design surfaces.
 *
 * Ported from the design system's `fmtNum` / `fmtSom` / `fmtShort`
 * (docs/GardEn/handoff-2026-06/garden/system.jsx). Russian grouping
 * (`158 000`) is used for RU + KY; EN swaps the `сом` suffix for `som`.
 *
 * `parseDecimal` exists because the API serialises Numeric columns
 * (prices, quantities) as JSON **strings** (Decimal → str — the
 * chat-product-cards Decimal-as-JSON-string contract). Call sites parse
 * once at the boundary, then format with the helpers below.
 */

/** `158000` → `"158 000"` (grouped with plain spaces, never NBSP/comma). */
export function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return "0";
  // ru-RU groups with NBSP / narrow-NBSP depending on the ICU version;
  // \s matches both — normalise every separator to a plain space.
  return Math.round(n).toLocaleString("ru-RU").replace(/\s/g, " ");
}

/** `1250` → `"1 250 сом"` (RU/KY) or `"1 250 som"` (EN). */
export function fmtSom(n: number, lang: string = "ru"): string {
  const unit = lang === "en" ? "som" : "сом";
  return `${fmtNum(n)} ${unit}`;
}

/**
 * A price BAND: `(5500, 14000)` → `"5 500 – 14 000 сом"` (`som` in EN).
 *
 * Half the catalogue sells in a band rather than at a point, and the unit is
 * written ONCE after the pair — "5 500 сом – 14 000 сом" reads as two separate
 * prices. The separator is an en dash with spaces, not a hyphen: `fmtNum`
 * already groups thousands with spaces, so a tight hyphen against "5 500"
 * reads as a minus sign.
 */
export function fmtSomRange(low: number, high: number, lang: string = "ru"): string {
  const unit = lang === "en" ? "som" : "сом";
  return `${fmtNum(low)} – ${fmtNum(high)} ${unit}`;
}

/** Compact form: `158000` → `"158k"`, `<1000` stays whole. */
export function fmtShort(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1000) return `${Math.round(n / 100) / 10}k`;
  return String(Math.round(n));
}

/**
 * Parse an API Decimal value (string or number) to a JS number.
 * Returns 0 for null/undefined/non-numeric so formatting never throws.
 */
export function parseDecimal(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}
